import { motion } from "framer-motion";
import { ShieldCheck, PiggyBank, Home, ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { KineticWords } from "./KineticWords";

const icons = { insurance: ShieldCheck, investments: PiggyBank, mortgages: Home };

const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

/** Entrance: fade-only on mobile (velké y/rotace překrývaly obsah pod kartami). */
const enterCard = (rotate, delay = 0) =>
  isMobile
    ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true, margin: "-30px" }, transition: { duration: 0.4, delay } }
    : { initial: { opacity: 0, y: 90, rotate }, whileInView: { opacity: 1, y: 0, rotate: 0 }, viewport: { once: true, margin: "-80px" }, transition: { type: "spring", stiffness: 90, damping: 16, delay } };

export const Services = () => {
  const { t, lang } = useLanguage();
  const featured = t.services.pillars[0];
  const rest = t.services.pillars.slice(1);

  return (
    <section id="services" data-testid="services-section" className="relative overflow-hidden py-28 sm:py-36 lg:py-44">
      <div className="pointer-events-none absolute left-1/3 top-0 h-[380px] w-[560px] rounded-full blur-[150px]" style={{ background: "radial-gradient(circle, rgba(0,245,212,0.07) 0%, transparent 70%)" }} aria-hidden="true" />
      <div className="mesh-orb orb-b absolute -right-40 top-0 h-[460px] w-[460px] rounded-full" style={{ background: "rgba(13,148,136,0.13)" }} aria-hidden="true" />
      <div className="mesh-orb orb-a absolute -left-32 bottom-10 h-[380px] w-[380px] rounded-full" style={{ background: "rgba(30,58,95,0.5)" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "10%", top: "22%", width: 3, height: 3, animationDelay: "0.4s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "86%", top: "65%", width: 2, height: 2, animationDelay: "1.5s" }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12">
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-teal-400">
          02 · {t.services.badge[lang]}
        </span>
        <h2
          data-testid="services-heading"
          className="font-display mt-6 max-w-5xl text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <KineticWords key={lang} text={t.services.heading[lang]} />
        </h2>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{t.services.subtext[lang]}</p>

        <div className="mt-20 grid gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 90, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ type: "spring", stiffness: 90, damping: 16 }}
            className="lg:col-span-7"
          >
            <div
              data-testid="service-card-insurance"
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-teal-600/30 bg-[#0A192F] p-8 transition-transform duration-300 hover:-translate-y-1.5 sm:p-12"
            >
              <span aria-hidden="true" className="text-outline-teal font-display pointer-events-none absolute -right-6 -top-12 select-none text-[10rem] font-black leading-none opacity-25 sm:text-[12rem]">
                01
              </span>
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-teal-400/15 blur-[80px]" aria-hidden="true" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-[#1E3A5F]/70 blur-[90px]" aria-hidden="true" />
              <div className="relative flex items-center justify-between">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-500/15 text-teal-300 transition-transform duration-300 group-hover:scale-110">
                  <ShieldCheck size={26} strokeWidth={1.8} />
                </div>
                <span className="rounded-full border border-teal-400/40 bg-teal-400/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-300">
                  {t.services.mainFocus[lang]}
                </span>
              </div>
              <h3 className="font-display relative mt-8 text-2xl font-bold text-white sm:text-4xl">{featured.title[lang]}</h3>
              <p className="relative mt-4 flex-1 text-base leading-relaxed text-slate-300">{featured.desc[lang]}</p>
              <div className="relative mt-6 flex flex-wrap gap-2">
                {featured.tags.map((tag, i) => (
                  <span
                    key={i}
                    data-testid={`insurance-tag-${i}`}
                    className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-slate-200 transition-colors duration-200 hover:border-teal-400/50"
                  >
                    {tag[lang]}
                  </span>
                ))}
              </div>
              <a
                href="#booking"
                data-testid="service-cta-insurance"
                className="relative mt-9 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0D9488] px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#14B8A6]"
              >
                {t.services.cta[lang]}
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>

          <div className="grid gap-6 lg:col-span-5">
            {rest.map((p, i) => {
              const Icon = icons[p.id];
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 90, rotate: 2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ type: "spring", stiffness: 90, damping: 16, delay: 0.12 + i * 0.15 }}
                >
                  <div
                    data-testid={`service-card-${p.id}`}
                    className="glass-dark group relative h-full overflow-hidden rounded-3xl p-8 hover:-translate-y-1 sm:p-10"
                  >
                    <span aria-hidden="true" className="text-outline-white font-display pointer-events-none absolute -right-3 -top-9 select-none text-[7rem] font-black leading-none opacity-40">
                      0{i + 2}
                    </span>
                    <div className="relative flex items-start gap-5">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0A192F] text-teal-300 transition-all duration-300 group-hover:bg-teal-600 group-hover:text-white group-hover:shadow-[0_0_20px_rgba(13,148,136,0.4)]">
                        <Icon size={22} strokeWidth={1.8} />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">{p.title[lang]}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc[lang]}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {p.tags.map((tag, j) => (
                            <span key={j} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-slate-300">
                              {tag[lang]}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
